import React, { useEffect, useMemo } from "react";
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

/**
 * Learner-facing players for the multi-part question types.
 *
 * Kept out of `AssessmentSection` because each one owns a non-trivial bit of
 * presentation logic — shuffling, reordering, per-gap inputs — that has nothing
 * to do with the surrounding quiz flow (timer, navigation, submission).
 */

/**
 * Deterministic shuffle, seeded by the question id.
 *
 * A plain `Math.random()` sort would re-shuffle on EVERY render, so the answer
 * a learner was halfway through picking would jump around under the cursor.
 * Seeding on the question id gives a stable order for the whole attempt while
 * still differing between questions.
 */
const seededShuffle = (items, seed) => {
  let hash = 0;
  for (let i = 0; i < String(seed).length; i++) {
    hash = (hash * 31 + String(seed).charCodeAt(i)) >>> 0;
  }
  const next = () => {
    // xorshift32 — small, fast, and good enough to scramble a dozen items.
    hash ^= hash << 13;
    hash >>>= 0;
    hash ^= hash >> 17;
    hash ^= hash << 5;
    hash >>>= 0;
    return hash / 0xffffffff;
  };

  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const cardSx = {
  borderRadius: 3,
  bgcolor: "#f8f9fa",
  p: 2,
  "&:hover": { boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.12)" },
};

/* --------------------------- Match the following -------------------------- */

export const MatchFollowingPlayer = ({ question, response, onChange }) => {
  const pairs = useMemo(() => question.pairs || [], [question]);
  // The right-hand items are the pool to choose from, shuffled so the correct
  // pairing is not simply "row 1 with row 1".
  // The server now sends an already-shuffled pool and withholds the pairing;
  // the local shuffle stays as a fallback for older payloads.
  const choices = useMemo(
    () =>
      question.choices?.length
        ? question.choices
        : seededShuffle(pairs.map((pair) => pair.right), question._id),
    [pairs, question.choices, question._id]
  );

  const current = response?.matchAnswers || [];
  const valueFor = (left) =>
    current.find((entry) => entry.left === left)?.right || "";

  const select = (left, right) => {
    const rest = current.filter((entry) => entry.left !== left);
    onChange(right ? [...rest, { left, right }] : rest);
  };

  return (
    <Stack spacing={1.5}>
      <Typography fontSize="1rem">
        Match each item on the left with its pair:
      </Typography>
      {pairs.map((pair, index) => (
        <Paper key={index} elevation={0} sx={cardSx}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ sm: "center" }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" flex={1}>
              <Chip label={index + 1} size="small" />
              <Typography>{pair.left}</Typography>
            </Stack>
            <Select
              size="small"
              displayEmpty
              value={valueFor(pair.left)}
              onChange={(e) => select(pair.left, e.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 260 }, bgcolor: "white" }}
            >
              <MenuItem value="">
                <em>Choose a match</em>
              </MenuItem>
              {choices.map((choice, i) => (
                <MenuItem key={i} value={choice}>
                  {choice}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

/* ---------------------------- Fill in the blanks -------------------------- */

export const FillBlankPlayer = ({ question, response, onChange }) => {
  const count = (question.blanks || []).length;
  const answers = response?.blankAnswers || [];

  const setAt = (index, value) => {
    const next = Array.from({ length: count }, (_, i) => answers[i] || "");
    next[index] = value;
    onChange(next);
  };

  return (
    <Stack spacing={1.5}>
      <Typography fontSize="1rem">
        Fill in {count === 1 ? "the gap" : `the ${count} gaps`}:
      </Typography>
      {Array.from({ length: count }).map((_, index) => (
        <Paper key={index} elevation={0} sx={cardSx}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip label={`Gap ${index + 1}`} size="small" color="primary" />
            <TextField
              fullWidth
              size="small"
              placeholder="Your answer"
              value={answers[index] || ""}
              onChange={(e) => setAt(index, e.target.value)}
              sx={{ bgcolor: "white" }}
            />
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

/* -------------------------------- Ordering -------------------------------- */

export const OrderingPlayer = ({ question, response, onChange }) => {
  const options = useMemo(
    () => (question.options || []).map((option) => option.text),
    [question]
  );
  // Start from a shuffled sequence so the authored order is never the default
  // answer — otherwise every learner would score full marks by doing nothing.
  const shuffled = useMemo(
    () => seededShuffle(options, question._id),
    [options, question._id]
  );
  const current = response?.orderedOptions?.length
    ? response.orderedOptions
    : shuffled;

  // Record the displayed order immediately, not just after a swap — otherwise a
  // learner who leaves the shuffled order untouched submits orderedOptions: [],
  // which grades as fully wrong and renders as "blank" on the answer sheet even
  // when the shown order happens to be correct.
  useEffect(() => {
    if (!response?.orderedOptions?.length) {
      onChange(shuffled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shuffled]);

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= current.length) return;
    const next = [...current];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <Stack spacing={1.5}>
      <Typography fontSize="1rem">
        Put these in the correct order, top to bottom:
      </Typography>
      {current.map((text, index) => (
        <Paper key={text} elevation={0} sx={cardSx}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip label={index + 1} size="small" color="primary" />
            <Typography flex={1}>{text}</Typography>
            <Box>
              <IconButton
                size="small"
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={index === current.length - 1}
                onClick={() => move(index, 1)}
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

/**
 * Has this question been answered? Drives the progress dots.
 *
 * One function rather than the two copies of an inline boolean expression that
 * used to sit in the dot's `bgcolor` and its `color` — they had to agree, and a
 * new question type would have had to be added to both.
 */
export const isAnswered = (response) => {
  if (!response) return false;
  switch (response.type) {
    case "multipleChoice":
      return !!response.selectedOptions?.length;
    case "singleChoice":
      return !!response.selectedOption;
    case "yesNo":
      return !!response.yesNoAnswer;
    case "trueFalse":
      return !!response.trueFalseAnswer;
    case "descriptive":
      return (
        !!response.descriptiveAnswer &&
        response.descriptiveAnswer.trim() !== "<p><br></p>"
      );
    case "matchFollowing":
      return !!response.matchAnswers?.length;
    case "fillBlank":
      return !!response.blankAnswers?.some((value) => value && value.trim());
    case "ordering":
      // Ordering starts pre-populated with a shuffle, so "answered" means the
      // learner actually touched it.
      return !!response.orderedOptions?.length;
    default:
      return false;
  }
};
